'use client';

import { Check } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/utils';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from './button';

export interface ComboboxProps {
  options: Array<{
    value: string;
    label: string;
  }>;
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  className?: string;
  buttonClassName?: string;
  contentClassName?: string;
}

export const Combobox = React.forwardRef<HTMLButtonElement, ComboboxProps>(
  (
    {
      options,
      value,
      onValueChange,
      placeholder = 'Select an option...',
      searchPlaceholder = 'Search...',
      emptyText = 'No results found.',
      className,
      buttonClassName,
      contentClassName,
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState('');

    React.useEffect(() => {
      if (open) {
        setSearch('');
      }
    }, [open]);

    const selectedOption = React.useMemo(() => options.find((option) => option.value === value), [options, value]);

    const handleSelect = React.useCallback(
      (currentValue: string): void => {
        onValueChange?.(currentValue === value ? '' : currentValue);
        setOpen(false);
        setSearch(selectedOption?.label ?? '');
      },
      [onValueChange, value, selectedOption],
    );

    const filteredOptions = React.useMemo(() => {
      if (!search) return options;
      return options.filter((option) => option.label.toLowerCase().includes(search.toLowerCase()));
    }, [options, search]);

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant='outline'
            role='combobox'
            aria-expanded={open}
            className={cn('w-full justify-between !text-primary', buttonClassName)}
          >
            {selectedOption?.label ?? placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent className={cn('w-[var(--radix-popover-trigger-width)] p-0', contentClassName)} align='start'>
          <Command className={className}>
            <CommandInput placeholder={searchPlaceholder} value={search} onValueChange={setSearch} />
            <CommandList>
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {filteredOptions.map((option) => (
                  <CommandItem key={option.value} value={option.value} onSelect={handleSelect}>
                    <Check className={cn('mr-2 h-4 w-4', value === option.value ? 'opacity-100' : 'opacity-0')} />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  },
);

Combobox.displayName = 'Combobox';
